/**
 * ChauffeurProfilPage — Profil Hub + Espace RH Module
 * Hub de navigation: Avatar, infos, badge statut, menu items.
 * Sous-vues internes: Modifier profil, Documents, Véhicules, Notifications, Espace RH, Bug report.
 * Espace RH (internes uniquement): 6 onglets (Accueil, Planning, Congés, Documents, Bulletins, Historique).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useProfileCache } from '../../hooks/useProfileCache';
import axios from 'axios';
import {
  User, FileText, ShieldCheck, Loader2, Car, Phone, Mail,
  CheckCircle, AlertCircle, Clock, XCircle, Upload, Camera, Trash2,
  ChevronLeft, ChevronRight, Pencil, Bell, Bug, Briefcase, Sun, Moon,
  Calendar, PalmtreeIcon, FolderOpen, Receipt, History
} from 'lucide-react';
import config from 'config';

// Sub-view components
import ProfilMenuView from './profil/ProfilMenuView';
import EditProfileView from './profil/EditProfileView';
import DocumentsView from './profil/DocumentsView';
import NotificationsView from './profil/NotificationsView';
import BugReportView from './profil/BugReportView';
import EspaceRHView from './profil/EspaceRHView';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const STATUS_BADGE = {
  ACTIVE: { label: 'Actif', icon: CheckCircle, cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', dotCls: 'bg-emerald-400' },
  PENDING_VERIFICATION: { label: 'En attente de validation', icon: Clock, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', dotCls: 'bg-amber-400' },
  UNDER_REVIEW: { label: 'En cours de vérification', icon: Clock, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', dotCls: 'bg-amber-400' },
  REJECTED: { label: 'Rejeté', icon: XCircle, cls: 'bg-red-500/15 text-red-400 border-red-500/30', dotCls: 'bg-red-400' },
  SUSPENDED: { label: 'Suspendu', icon: AlertCircle, cls: 'bg-red-500/15 text-red-400 border-red-500/30', dotCls: 'bg-red-400' },
};

export default function ChauffeurProfilPage() {
  const { user } = useAuth();
  const [view, setView] = useState('menu'); // menu | edit | documents | notifications | hr | bug
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  const driverStatus = user?.driver_status || 'PENDING_VERIFICATION';
  const badge = STATUS_BADGE[driverStatus] || STATUS_BADGE.PENDING_VERIFICATION;
  const BadgeIcon = badge.icon;
  const isInterne = user?.fleet_status === 'interne';

  // Load profile photo
  useEffect(() => {
    const loadPhoto = async () => {
      try {
        const res = await axios.get(`${API}/driver/profile-photo`, { headers: getHeaders() });
        if (res.data?.photo_url) setPhotoUrl(res.data.photo_url);
      } catch { /* no photo */ }
    };
    loadPhoto();
  }, []);

  // Load unread notification count (for internal employees)
  useEffect(() => {
    if (!isInterne) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/employees/me/notifications?unread_only=true`, { headers: getHeaders() });
        setUnreadNotifs(res.data?.unread_count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
  }, [isInterne, view]);

  const handlePhotoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('La photo ne doit pas dépasser 5 Mo.'); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { alert('Format non supporté. Utilisez JPG, PNG ou WebP.'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    setPhotoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      const res = await axios.post(`${API}/driver/profile-photo`, formData, { headers: getHeaders() });
      setPhotoUrl(res.data.photo_url);
      setPhotoPreview(null);
      setPhotoFile(null);
      setShowPhotoPicker(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur upload photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm('Supprimer votre photo de profil ?')) return;
    try {
      await axios.delete(`${API}/driver/profile-photo`, { headers: getHeaders() });
      setPhotoUrl(null);
    } catch { /* ignore */ }
  };

  // Back navigation handler
  const goBack = () => setView('menu');

  // Sub-view header
  const SubViewHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-3 mb-4">
      <button onClick={goBack} className="p-2 -ml-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation" data-testid="profil-back-btn">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#C9A24D]" />}
        <h2 className="text-white font-semibold text-base">{title}</h2>
      </div>
    </div>
  );

  // ─── Render sub-views ───
  if (view === 'edit') {
    return (
      <div className="px-4 py-5 max-w-lg mx-auto" data-testid="profil-edit-view">
        <SubViewHeader title="Modifier mon profil" icon={Pencil} />
        <EditProfileView user={user} onBack={goBack} />
      </div>
    );
  }

  if (view === 'documents') {
    return (
      <div className="px-4 py-5 max-w-lg mx-auto" data-testid="profil-documents-view">
        <SubViewHeader title="Mes Documents" icon={FileText} />
        <DocumentsView user={user} />
      </div>
    );
  }

  if (view === 'notifications') {
    return (
      <div className="px-4 py-5 max-w-lg mx-auto" data-testid="profil-notifications-view">
        <SubViewHeader title="Notifications" icon={Bell} />
        <NotificationsView />
      </div>
    );
  }

  if (view === 'hr') {
    return (
      <div className="px-4 py-5 max-w-lg mx-auto" data-testid="profil-hr-view">
        <SubViewHeader title="Espace RH" icon={Briefcase} />
        <EspaceRHView user={user} />
      </div>
    );
  }

  if (view === 'bug') {
    return (
      <div className="px-4 py-5 max-w-lg mx-auto" data-testid="profil-bug-view">
        <SubViewHeader title="Signaler un bug" icon={Bug} />
        <BugReportView user={user} onBack={goBack} />
      </div>
    );
  }

  // ─── Main Menu View ───
  return (
    <div className="px-4 py-5 max-w-lg mx-auto space-y-4" data-testid="chauffeur-profil-page">
      {/* Profile Header with Photo & Status Badge */}
      <div className="flex items-center gap-3.5" data-testid="profile-header">
        <div className="relative shrink-0" data-testid="profile-photo-area">
          <button
            onClick={() => setShowPhotoPicker(true)}
            className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#C9A24D]/30 hover:border-[#C9A24D] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C9A24D]/50"
            data-testid="profile-photo-btn"
          >
            {photoUrl ? (
              <img src={photoUrl} alt="Photo de profil" className="w-full h-full object-cover" data-testid="profile-photo-img" />
            ) : (
              <div className="w-full h-full bg-[#C9A24D]/12 flex items-center justify-center">
                <User className="w-7 h-7 text-[#C9A24D]" />
              </div>
            )}
          </button>
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-[#C9A24D] rounded-full flex items-center justify-center border-2 border-[#0B0B0B]">
            <Camera className="w-2.5 h-2.5 text-black" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold text-base truncate">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-white/40 text-xs truncate">{user?.email}</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${badge.dotCls}`} />
            <Badge className={`text-[10px] border font-semibold ${badge.cls}`} data-testid="account-status-badge">
              <BadgeIcon className="w-3 h-3 mr-1" />
              {badge.label}
            </Badge>
          </div>
        </div>
      </div>

      {/* Photo Picker Modal */}
      {showPhotoPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="photo-picker-modal">
          <div className="bg-[#1F1F1F] w-full sm:max-w-sm sm:rounded-xl rounded-t-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold text-sm">Photo de profil</h3>
              <button onClick={() => { setShowPhotoPicker(false); setPhotoPreview(null); setPhotoFile(null); }} className="text-white/40 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 flex justify-center">
              <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-[#C9A24D]/30 bg-[#0B0B0B]">
                {photoPreview ? (
                  <img src={photoPreview} alt="Apercu" className="w-full h-full object-cover" data-testid="photo-preview" />
                ) : photoUrl ? (
                  <img src={photoUrl} alt="Photo actuelle" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><User className="w-12 h-12 text-white/20" /></div>
                )}
              </div>
            </div>
            <div className="p-4 space-y-2">
              {photoPreview ? (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 border-white/10 text-white min-h-[48px]" onClick={() => { setPhotoPreview(null); setPhotoFile(null); }} disabled={photoLoading}>Annuler</Button>
                  <Button className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold min-h-[48px]" onClick={handlePhotoUpload} disabled={photoLoading} data-testid="photo-upload-confirm">
                    {photoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" />Enregistrer</>}
                  </Button>
                </div>
              ) : (
                <>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/8 cursor-pointer transition-colors min-h-[48px] touch-manipulation" data-testid="photo-gallery-btn">
                    <Upload className="w-5 h-5 text-[#C9A24D]" />
                    <span className="text-white text-sm">Choisir depuis la galerie</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoSelected} />
                  </label>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 hover:bg-white/8 cursor-pointer transition-colors min-h-[48px] touch-manipulation" data-testid="photo-camera-btn">
                    <Camera className="w-5 h-5 text-[#C9A24D]" />
                    <span className="text-white text-sm">Prendre une photo</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" onChange={handlePhotoSelected} />
                  </label>
                  {photoUrl && (
                    <button onClick={() => { handlePhotoDelete(); setShowPhotoPicker(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/8 hover:bg-red-500/15 transition-colors w-full min-h-[48px] touch-manipulation"
                      data-testid="photo-delete-btn">
                      <Trash2 className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 text-sm">Supprimer la photo</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Menu Items */}
      <Card className="bg-[#161616] border-white/6">
        <CardContent className="p-2" data-testid="profil-menu-items">
          <MenuItem icon={Pencil} label="Modifier mon profil" onClick={() => setView('edit')} testId="menu-edit-profile" />
          <MenuItem icon={FileText} label="Mes Documents" onClick={() => setView('documents')} testId="menu-documents" />
          {!isInterne && (
            <MenuItem icon={Car} label="Mes Vehicules" onClick={() => setView('vehicles')} testId="menu-vehicles" />
          )}
          {isInterne && (
            <MenuItem icon={Bell} label="Notifications" onClick={() => setView('notifications')} badge={unreadNotifs} testId="menu-notifications" />
          )}
          {isInterne && (
            <MenuItem icon={Briefcase} label="Espace RH" onClick={() => setView('hr')} testId="menu-espace-rh" accentColor="text-[#C9A24D]" />
          )}
          <MenuItem icon={Bug} label="Signaler un bug" onClick={() => setView('bug')} testId="menu-bug-report" />
        </CardContent>
      </Card>

      {/* Account Status Card */}
      <Card className="bg-[#161616] border-white/6">
        <CardContent className="p-4" data-testid="profile-account-status">
          <h3 className="text-sm font-semibold text-[#C9A24D] mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Statut du compte
          </h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-white/40">Statut</span>
              <Badge className={`text-[10px] border ${badge.cls}`}>{badge.label}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/40">Type</span>
              <span className="text-white/70 text-xs">{isInterne ? 'Salarié interne' : 'Indépendant'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/40">Email vérifié</span>
              <span className={user?.email_verified ? 'text-emerald-400 text-xs' : 'text-red-400 text-xs'}>
                {user?.email_verified ? 'Oui' : 'Non'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/40">Membre depuis</span>
              <span className="text-white/70 text-xs">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '--'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Deletion */}
      <div className="pt-2 pb-4">
        <button className="text-red-400/60 text-xs hover:text-red-400 transition-colors" data-testid="delete-account-btn">
          Supprimer mon compte
        </button>
      </div>
    </div>
  );
}

/* ─── Menu Item Component ─── */
function MenuItem({ icon: Icon, label, onClick, badge, testId, accentColor }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-3.5 rounded-lg hover:bg-white/5 active:bg-white/8 transition-colors min-h-[48px] touch-manipulation"
      data-testid={testId}
    >
      <Icon className={`w-5 h-5 ${accentColor || 'text-white/40'}`} />
      <span className="text-white text-sm font-medium flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`${testId}-badge`}>
          {badge}
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-white/20" />
    </button>
  );
}
